import { useMemo } from "react";
import validUrl from "valid-url";

import {
  IntegrationCustomization,
  OrganizationCustomization,
} from "../context/CustomizationContext";
import { AUTH_MESSAGE_MAX_LENGTH } from "../cards/ConfigurationHelpGuideURLCard";
import {
  configurationCustomizationKeys,
  featuresCustomizationKeys,
  getIntegrationCustomizationCounts,
  getOrganizationCustomizationsCounts,
  appearanceCustomizationKeys,
  brandingCustomizationKeys,
} from "./utils";

type CustomizationChangeCount = {
  appearanceCount: number;
  featuresCount: number;
  configurationCount: number;
  brandingCount: number;
  publishDisabled: boolean;
  hasValidationError: boolean;
};

const useCustomizationChangeInformation = (
  organizationCustomization: OrganizationCustomization | null,
  originalOrganizationCustomization: OrganizationCustomization | null,
  integrationCustomizations: IntegrationCustomization[] | null,
  originalIntegrationCustomizations: IntegrationCustomization[] | null,
  logo: File | string | null,
  hasLogoChange: boolean,
) => {
  const customizationChangeCounts: CustomizationChangeCount = useMemo(() => {
    const appearanceCount = getOrganizationCustomizationsCounts(
      appearanceCustomizationKeys,
      organizationCustomization,
      originalOrganizationCustomization,
    );
    const featuresCount = getOrganizationCustomizationsCounts(
      featuresCustomizationKeys,
      organizationCustomization,
      originalOrganizationCustomization,
    );
    const configurationCount =
      getOrganizationCustomizationsCounts(
        configurationCustomizationKeys,
        organizationCustomization,
        originalOrganizationCustomization,
      ) +
      getIntegrationCustomizationCounts(
        integrationCustomizations,
        originalIntegrationCustomizations,
      );
    let brandingCount = getOrganizationCustomizationsCounts(
      brandingCustomizationKeys,
      organizationCustomization,
      originalOrganizationCustomization,
    );
    // handle logo
    if (hasLogoChange) {
      brandingCount++;
    }
    // handle white label guide
    if (
      organizationCustomization?.show_white_label_guide !== undefined &&
      organizationCustomization.show_white_label_guide !==
        originalOrganizationCustomization?.white_label_guide_configuration?.active
    ) {
      brandingCount++;
    }
    // if there are any urls that are invalid or any message that is too large do not let the user publish
    const hasValidationError =
      integrationCustomizations?.some((integObj) => {
        const {
          help_center_authentication_message,
          help_center_authentication_url,
          help_center_validation_url,
        } = integObj;
        if (
          help_center_authentication_url != null &&
          help_center_authentication_url != "" &&
          validUrl.isWebUri(help_center_authentication_url) === undefined
        ) {
          return true;
        }
        if (
          help_center_validation_url != null &&
          help_center_validation_url != "" &&
          validUrl.isWebUri(help_center_validation_url) === undefined
        ) {
          return true;
        }
        if ((help_center_authentication_message ?? '"').length > AUTH_MESSAGE_MAX_LENGTH) {
          return true;
        }
        return false;
      }) ?? false;

    const publishDisabled =
      hasValidationError ||
      (appearanceCount === 0 &&
        featuresCount === 0 &&
        configurationCount === 0 &&
        brandingCount === 0);
    return {
      appearanceCount,
      featuresCount,
      configurationCount,
      brandingCount,
      publishDisabled,
      hasValidationError,
    };
  }, [
    organizationCustomization,
    originalOrganizationCustomization,
    integrationCustomizations,
    originalIntegrationCustomizations,
    logo,
  ]);

  return customizationChangeCounts;
};

export default useCustomizationChangeInformation;
